import { useState, useEffect } from 'react';
import { useCommonOptions } from './useCommonOptions';
import { CustomMapping } from '../types/mapping';
import { useApi } from '../services/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface DynamicMapping {
  vatZones: Record<string, string>;
  paymentTerms: Record<string, string>;
  customerGroups: Record<string, string>;
  currencies: Record<string, string>;
  units: Record<string, string>;
  accountTypeOptions: Record<string, string>;
  vatCodes: Record<string, string>;
  entryTypeOptions: Record<string, string>;
}

interface UseDynamicMappingsReturn {
  mappings: DynamicMapping;
  customMappings: CustomMapping[];
  isLoading: boolean;
  error: string | null;
  getMappedValue: (field: string, value: string, item?: any) => string;
  addCustomMapping: (mapping: CustomMapping) => Promise<void>;
  removeCustomMapping: (id: string) => Promise<void>;
}

export const useDynamicMappings = (): UseDynamicMappingsReturn => {
  const { currencies, paymentTerms, vatZones, customerGroups, units, isLoading, accountTypeOptions, vatCodes, entryTypeOptions } = useCommonOptions();
  const [mappings, setMappings] = useState<DynamicMapping>({
    vatZones: {},
    paymentTerms: {},
    customerGroups: {},
    currencies: {},
    units: {},
    accountTypeOptions: {},
    vatCodes: {},
    entryTypeOptions: {}
  });
  const [customMappings, setCustomMappings] = useState<CustomMapping[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const queryClient = useQueryClient();

  const { data, isLoading: isQueryLoading } = useQuery(
    ['customMappings'],
    async () => {
      const response = await api.getFieldMapping();
      return response?.data?.data ?? [];
    },
    {
      onSuccess: (data) => {
        setCustomMappings(data);
        setError(null);
      },
      onError: (err) => {
        console.error('Error loading custom mappings:', err);
        setError('Failed to load custom mappings');
      },
    }
  );

  useEffect(() => {
    if (!isLoading) {
      setMappings({
        vatZones: vatZones.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        paymentTerms: paymentTerms.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        customerGroups: customerGroups.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        currencies: currencies.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        units: units.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        accountTypeOptions: accountTypeOptions.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        vatCodes: vatCodes.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        entryTypeOptions: entryTypeOptions.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>)
      });
    }
  }, [currencies, paymentTerms, vatZones, customerGroups, units, isLoading, accountTypeOptions, vatCodes, entryTypeOptions]);

  const getMappedValue = (field: string, value: string, item?: any): string => {
    // if (value === null) return value;
    if(value === null || value === undefined) value = "";
    for (const customMapping of customMappings) {
      if (customMapping.field === field) {
        if (customMapping.mappings.length > 0) {
          for (const mapping of customMapping.mappings) {
            // if(field === "entryTypeNumber"){
            //   console.log(String(mapping.field),mapping.value, String(value), item);
            // }
            let mappingField = mapping.field === undefined || mapping.field === null ? "" : String(mapping.field);
            if (mappingField === String(value)) {
              if (mapping.extraFields) {
                let matchAll = true;
                for (const extraField of mapping.extraFields) {
                  const index = mapping.extraFields.indexOf(extraField);
                  const extraValue = String(mapping.extraValues[index]);
                  const itemValue = String(item[extraField]??'');
                  if(extraValue === "empty"){
                    if(itemValue !== ""){
                      matchAll = false;
                    }
                  }else if(extraValue === "not_empty"){
                    if(itemValue === ""){
                      matchAll = false;
                    }
                  }else if (itemValue !== extraValue) {
                    matchAll = false;
                  }
                }

                if(matchAll){
                  return mapping.value;
                }
              } else {
                return mapping.value;
              }
            }
          }
        }
      }
    }
    return value;
  };

  const addCustomMapping = async (mapping: CustomMapping) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.createFieldMapping(mapping);
      setCustomMappings(prevMappings => {
        const existingIndex = prevMappings.findIndex(m => m.field === mapping.field);
        if (existingIndex >= 0) {
          const updatedMappings = [...prevMappings];
          updatedMappings[existingIndex] = { ...updatedMappings[existingIndex], ...response.data.data };
          return updatedMappings;
        }
        return [...prevMappings, response.data.data];
      });
      queryClient.invalidateQueries(['customMappings']);
    } catch (err) {
      setError('Failed to add custom mapping');
      console.error('Error adding custom mapping:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeCustomMapping = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await api.deleteFieldMapping(id);
      setCustomMappings(prevMappings => prevMappings.filter(m => m.id !== id));
      queryClient.invalidateQueries(['customMappings']);
    } catch (err) {
      setError('Failed to remove custom mapping');
      console.error('Error removing custom mapping:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    mappings,
    customMappings,
    isLoading: isQueryLoading,
    error,
    getMappedValue,
    addCustomMapping,
    removeCustomMapping
  };
}; 